'use client';

import { type ReactNode, createContext, useContext, useEffect, useRef } from 'react';

import { useStore } from 'zustand';

import { type HeaderStore, createHeaderStore, initHeaderStore } from '@/shared/components/header/stores/header-store';

export type HeaderStoreApi = ReturnType<typeof createHeaderStore>;

export const HeaderStoreContext = createContext<HeaderStoreApi | undefined>(undefined);

export interface HeaderStoreProviderProps {
    children: ReactNode;
}

export const HeaderStoreProvider = ({ children }: HeaderStoreProviderProps) => {
    const storeRef = useRef<HeaderStoreApi>(null);
    if (!storeRef.current) {
        storeRef.current = createHeaderStore(initHeaderStore());
    }

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            storeRef.current?.getState().setScrollY(scrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        // Set initial scroll position
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return <HeaderStoreContext.Provider value={storeRef.current}>{children}</HeaderStoreContext.Provider>;
};

export const useHeaderStore = <T,>(selector: (store: HeaderStore) => T): T => {
    const headerStoreContext = useContext(HeaderStoreContext);

    if (!headerStoreContext) {
        throw new Error(`useCounterStore must be used within HeaderStoreProvider`);
    }

    return useStore(headerStoreContext, selector);
};
